import {
  ISquare,
  GameState,
  Position,
  IPiece,
  PieceType,
  Mode,
  State,
} from "../types";
import { Square } from "../Square";
import { Piece } from "../Piece";
import { getTutorialSpec } from "../../../components/Tutorials/TutorialSpecs";
import { FENHistoryManager } from "./FENHistoryManager";

const emptyFen = "8/8/8/8/8";

export class GameStateManager {
  private gameState: GameState;
  private tutorialStalemateCallback?: (data: any) => void;
  private fenHistoryManager: FENHistoryManager;

  constructor() {
    this.fenHistoryManager = new FENHistoryManager();
    this.gameState = {
      mode: Mode.Dev,
      state: State.PieceSelection,
      board: this.initializeBoard(),
      isLightTurn: true,
      turnCount: 0,
      fenHistory: [emptyFen],
    };
  }

  initializeBoard(): ISquare[][] {
    const rows = 8;
    const columns = 5;
    const board: ISquare[][] = [];

    for (let row = 0; row < rows; row++) {
      const reversedRow = rows - 1 - row;
      board[reversedRow] = new Array(columns)
        .fill(null)
        .map(() => this.getEmptySquare());
    }

    return board;
  }

  setupBoard(boardSetup: ISquare[][]): void {
    this.emptyBoard();

    if (boardSetup.length !== 8 || boardSetup.some((row) => row.length !== 5)) {
      throw new Error("Invalid board setup: Must be 8x5 dimensions");
    }

    for (let row = 0; row < boardSetup.length; row++) {
      for (let col = 0; col < boardSetup[row].length; col++) {
        const sourceSquare = boardSetup[row][col];

        if (!sourceSquare) {
          continue;
        }

        // CRITICAL FIX: Deep copy pieces and reset their state for new game
        // Shallow copy [...sourceSquare.Pieces] was preserving old state like hasLilyJumped=true
        const copiedPieces = sourceSquare.Pieces.map(piece => {
          const newPiece = new Piece(piece.type, piece.isLight, piece.direction);
          newPiece.isKing = piece.isKing;

          // Reset Frog lily jump ability for new game
          // Frogs on their home rank (0 for light, 7 for dark) should have lily ability
          if (piece.type === PieceType.Frog) {
            const homeRank = piece.isLight ? 0 : 7;
            newPiece.hasLilyJumped = row !== homeRank;
          }

          return newPiece;
        });

        this.gameState.board[row][col] = new Square(copiedPieces);
      }
    }
  }

  resetGame() {
    this.gameState = {
      mode: Mode.Dev,
      state: State.PieceSelection,
      board: this.initializeBoard(),
      isLightTurn: true,
      turnCount: 0,
      fenHistory: [emptyFen],
    };
  }

  getAllPieces(): IPiece[] {
    return this.gameState.board.reduce((acc, row) => {
      const rowPieces = row.reduce((rowAcc, square) => {
        return rowAcc.concat(square.Pieces);
      }, [] as IPiece[]);

      return acc.concat(rowPieces);
    }, [] as IPiece[]);
  }

  resetTurnCount() {
    this.gameState.turnCount = 0;
  }

  getBoardState(): ISquare[][] {
    return this.gameState.board;
  }

  getGameState(): GameState {
    return this.gameState;
  }

  setGameState(gameState: GameState): void {
    // this seems dangerous
    this.gameState = gameState;
  }

  setGameStateState(state: State): void {
    this.gameState.state = state;
  }

  setMode(mode: Mode) {
    this.gameState.mode = mode;
  }

  setMonkeyGambleFailure(
    failure:
      | {
          diceRoll: number;
          dangerCount: number;
          jumpedEnemies: Position[];
          bearCapturePosition?: Position;
        }
      | undefined
  ): void {
    this.gameState.lastMonkeyGambleFailure = failure;

    // Also set the loss effect position for the UI when failure occurs
    if (failure) {
      let lossEffectPosition = null;

      // If there's a bear capture attempt, prioritize showing the loss effect on the bear
      if (failure.bearCapturePosition) {
        lossEffectPosition = failure.bearCapturePosition;
      } else {
        // Original logic for jumped enemies
        if (failure.diceRoll === 1) {
          // Show loss effect on first enemy jumped over - manually set to row 4, col 2
          lossEffectPosition = { row: 4, col: 2 };
        } else if (failure.diceRoll === 2) {
          // Show loss effect on second enemy jumped over - manually set to row 6, col 2
          lossEffectPosition = { row: 6, col: 2 };
        }

        if (lossEffectPosition) {
        }
      }

      if (lossEffectPosition) {
        this.gameState.monkeyGambleLossEffectPosition = lossEffectPosition;
      }
    } else {
      // Clear the position when clearing failure
      this.gameState.monkeyGambleLossEffectPosition = undefined;
    }
  }

  nextGameState() {
    switch (this.gameState.state) {
      case State.Searching:
        this.gameState.state = State.KingSelection;
        break;
      case State.KingSelection:
        this.gameState.state = State.PieceSelection;
        break;
      case State.PieceSelection:
        this.gameState.state = State.Playing;
        break;
      case State.Playing:
        this.gameState.state = State.PostGame;
        break;
      default:
        break;
    }
  }

  setLightPlayer(playerId: string) {
    this.gameState.lightPlayer = playerId;
  }

  setDarkPlayer(playerId: string) {
    this.gameState.darkPlayer = playerId;
  }

  setLightPlayerUsername(username: string) {
    this.gameState.lightPlayerUsername = username;
  }

  setDarkPlayerUsername(username: string) {
    this.gameState.darkPlayerUsername = username;
  }

  changeTurns() {

    // CRITICAL FIX: Check for bonus turn here too, since this method can be called directly
    if (this.gameState.skipTurnChange) {
      return; // Don't change turns during bonus turn
    }

    this.gameState.isLightTurn = !this.gameState.isLightTurn;

    // CRITICAL: Clear bonus turn piece ID when turn actually changes
    // This removes virus/penguin selection lock after auto-end turn
    if (this.gameState.activeBonusTurnPieceId) {
      this.gameState.activeBonusTurnPieceId = null;
    }

    if (this.gameState.state === State.Playing) {
      // Check if we're in any tutorial mode (Tutorial mode OR TestAI mode with tutorialConfig)
      const tutorialConfig = (this.gameState as any).tutorialConfig;
      const isInTutorialMode =
        this.gameState.mode === Mode.Tutorial ||
        (this.gameState.mode === Mode.TestAI && tutorialConfig);

      if (isInTutorialMode) {
        const uiLogger = (tutorialConfig as any)?.uiLogger;
        // In tutorial modes, turn count is incremented BEFORE move in makeMove(), not here
        if (this.gameState.isLightTurn) {
          if (uiLogger) {
            uiLogger(
              `🔄 Turn changed to Light player - Turn: ${this.gameState.turnCount} (mode: ${this.gameState.mode})`,
              "info"
            );
          }
        } else {
          if (uiLogger) {
            uiLogger(
              `🔄 Turn changed to Dark (AI) - Turn stays: ${this.gameState.turnCount} (mode: ${this.gameState.mode})`,
              "info"
            );
          }
        }
      } else {
        // For non-Tutorial modes, increment normally
        this.incrementTurnCount();
      }
    }

    // Self-heal orphaned entanglement linkages. If a piece is flagged
    // isEntangled but no Octopus on the board owns it, clear the flag —
    // otherwise getValidMoves gates that piece to [] and the user can't
    // select it for the rest of the match.
    this.healOrphanedEntanglement();
  }

  private healOrphanedEntanglement(): void {
    const board = this.gameState.board;
    if (!board) return;

    const allPieces: IPiece[] = [];
    for (const row of board) {
      if (!row) continue;
      for (const square of row) {
        if (!square?.Pieces) continue;
        for (const piece of square.Pieces) allPieces.push(piece);
      }
    }

    const entanglerIdSet = new Set<string>();
    for (const piece of allPieces) {
      if (piece.type === PieceType.Octopus && piece.entangledPieceId) {
        entanglerIdSet.add(piece.entangledPieceId);
      }
    }

    const livePieceIdSet = new Set(allPieces.map((p) => p.id));

    for (const piece of allPieces) {
      if (piece.isEntangled && !entanglerIdSet.has(piece.id)) {
        piece.isEntangled = false;
      }
      if (
        piece.type === PieceType.Octopus &&
        piece.entangledPieceId &&
        !livePieceIdSet.has(piece.entangledPieceId)
      ) {
        piece.entangledPieceId = undefined;
      }
    }
  }

  incrementTurnCount() {
    // BONUS TURN PROTECTION: Don't increment turn count during active bonus turns
    // This ensures swing sequences (original move + bonus moves) count as single turns
    if (this.gameState.activeBonusTurnPieceId) {
      return;
    }

    this.gameState.turnCount++;
  }

  getPiecesAt(position: Position): IPiece[] {
    return this.gameState.board[position.row][position.col].Pieces;
  }

  getPieceAndPositionById(pieceId: string): {
    piece: IPiece;
    position: Position;
  } {
    var piece = this.getAllPieces().find((x) => x.id === pieceId);
    if (piece === undefined) throw new Error("Piece not found");
    var position = this.findPiecePositionById(pieceId);
    return { piece, position };
  }

  getEmptySquare(): ISquare {
    return new Square([]);
  }

  emptyBoard(): void {
    this.gameState.board.forEach((row) => {
      row.forEach((square) => {
        square.Pieces = [];
      });
    });

    // CRITICAL FIX: Clear bonus turn state when board is cleared
    // This prevents selection lock from persisting after clearing the board in sandbox
    if (this.gameState.activeBonusTurnPieceId || this.gameState.skipTurnChange) {
      this.clearBonusTurn();
    }
  }

  placePiece(piece: IPiece, position: Position): void {
    this.gameState.board[position.row][position.col].Pieces.push(piece);
  }

  removePiece(position: Position, piece: IPiece): void {
    const square = this.getSquare(position);
    const before = square.Pieces.length;
    square.Pieces = square.Pieces.filter((x) => x !== piece);
    const after = square.Pieces.length;
    if (before === after && before > 0) {
      // Reference mismatch! The piece wasn't found by reference equality.
      // Fallback: remove by type+side match to prevent merge bug
      const fallbackIdx = square.Pieces.findIndex(
        (p) => p.type === piece.type && p.isLight === piece.isLight
      );
      if (fallbackIdx !== -1) {
        square.Pieces.splice(fallbackIdx, 1);
      }
    }
  }

  addPiece(position: Position, piece: IPiece): void {
    const square = this.getSquare(position);
    square.Pieces = [...square.Pieces, piece];
  }

  getSquare(position: Position): ISquare {
    const { row, col } = position;
    if (
      row < 0 ||
      row >= this.gameState.board.length ||
      col < 0 ||
      col >= this.gameState.board[0].length
    ) {
      throw new Error("Position is out of bounds");
    }
    return this.gameState.board[row][col];
  }

  // new method to find piece position by id
  findPiecePositionById(pieceId: string): Position {
    for (let row = 0; row < this.gameState.board.length; row++) {
      for (let col = 0; col < this.gameState.board[row].length; col++) {
        const square = this.gameState.board[row][col];
        if (square.Pieces.some((piece) => piece.id === pieceId)) {
          return { row, col };
        }
      }
    }
    throw new Error("Piece not found on the board)");
  }

  findPiecePosition(piece: IPiece): Position | null {
    for (let row = 0; row < this.gameState.board.length; row++) {
      for (let col = 0; col < this.gameState.board[row].length; col++) {
        const square = this.gameState.board[row][col];
        if (square.Pieces.some((p) => p.id === piece.id)) {
          return { row, col };
        }
      }
    }
    return null;
  }

  findPieceById(pieceId: string): IPiece | null {
    for (let row = 0; row < this.gameState.board.length; row++) {
      for (let col = 0; col < this.gameState.board[row].length; col++) {
        const square = this.gameState.board[row][col];
        const piece = square.Pieces.find((p) => p.id === pieceId);
        if (piece) {
          return piece;
        }
      }
    }
    return null;
  }

  setStartingPiece(pieceType: PieceType, isLight: boolean, col: number): void {
    // validate
    if (this.gameState.state !== State.PieceSelection)
      throw Error("The game state is incorrect to set a starting piece");

    if (col < 0 || col > 4) throw new Error("invalid column");

    const row = isLight ? 0 : 7;
    const square = this.getSquare({ row, col });

    if (square.Pieces[0]) {
      throw new Error(
        `There is already a ${square.Pieces[0].type} in this column`
      );
    }

    var placedPieces = this.getPiecesInRow(row).map((x) => x.type);

    if (placedPieces.includes(pieceType))
      throw new Error(`A ${pieceType} has already been placed`);

    var isPlacingLastPiece = placedPieces.length === 4;
    var king = isLight ? this.gameState.lightKing! : this.gameState.darkKing!;
    var kingHasBeenPlaced = placedPieces.includes(king);

    if (isPlacingLastPiece && !kingHasBeenPlaced && pieceType !== king)
      throw new Error(`King needs to be placed, please place your ${king}`);

    // do work
    const piece = new Piece(pieceType, isLight);

    // Set king flag if this piece is the designated king
    if (isLight && pieceType === this.gameState.lightKing) {
      piece.isKing = true;
    } else if (!isLight && pieceType === this.gameState.darkKing) {
      piece.isKing = true;
    }

    // Set penguin direction: light penguins face down, dark penguins face up
    if (pieceType === PieceType.Penguin) {
      piece.direction = isLight ? 'down' : 'up';
    }

    this.placePiece(piece, { row, col });
    this.changeTurns();

    // check if we should move to playing
    var allLightPiecesPlaced = this.getPiecesInRow(0).length === 5;
    var allDarkPiecesPlaced = this.getPiecesInRow(7).length === 5;
    if (allLightPiecesPlaced && allDarkPiecesPlaced) {
      this.nextGameState();
    }
  }

  getPiecesInRow(row: number): IPiece[] {
    return Array.from(
      { length: 5 },
      (_, col) => this.getSquare({ row, col })?.Pieces ?? []
    ).flat();
  }

  // method works okay in basic cases, but will fail in more complex cases, eg zombified piece
  hasGameEnded(isSimulation: boolean = false): boolean {
    // SANDBOX MODE: Never end the game - sandbox is for testing piece interactions only
    if (this.gameState.mode === Mode.Sandbox) {
      return false;
    }

    var pieces = this.getAllPieces();

    // CRITICAL FIX: Only modify game state when NOT in simulation mode
    if (!isSimulation) {
      // Reset zombie race piece type each check
      this.gameState.zombieRacePieceType = undefined;
    }

    // Check for light king
    var isLightKingPresent = pieces.some(
      (x) => x.isLight && x.type === this.gameState.lightKing
    );
    // Check for dark king
    var isDarkKingPresent = pieces.some(
      (x) => !x.isLight && x.type === this.gameState.darkKing
    );

    // --- ZOMBIE LOGIC ---
    // If the king is not present, but a zombified enemy piece exists, treat the game as NOT ended
    // Light king check: if no light king, but a zombified dark piece exists
    if (!isLightKingPresent) {
      const hasZombifiedDark = pieces.some(
        (x) =>
          !x.isLight &&
          x.zombifiedStatus.isZombified &&
          x.type !== this.gameState.darkKing
      );
      if (hasZombifiedDark) {
        isLightKingPresent = true; // Treat as if king is present
      }
    }
    // Dark king check: if no dark king, but a zombified light piece exists
    if (!isDarkKingPresent) {
      const hasZombifiedLight = pieces.some(
        (x) =>
          x.isLight &&
          x.zombifiedStatus.isZombified &&
          x.type !== this.gameState.lightKing
      );
      if (hasZombifiedLight) {
        isDarkKingPresent = true; // Treat as if king is present
      }
    }

    //add zombie race logic here

    // --- ZOMBIE KING RACE LOGIC ---
    // If the king is a Fungi, check for a zombified animal (not Fungi) on the home rank
    // Light side: check row 7 for a zombified dark animal if light king is Fungi
    if (this.gameState.lightKing === PieceType.Fungi) {
      const zombieKingOnRow7 = this.getPiecesInRow(7).find(
        (x) =>
          !x.isLight &&
          x.zombifiedStatus.isZombified &&
          x.type !== PieceType.Fungi
      );
      if (zombieKingOnRow7) {
        // CRITICAL FIX: Only modify game state when NOT in simulation
        if (!isSimulation) {
          this.gameState.lightWon = true;
          this.gameState.zombieRacePieceType = zombieKingOnRow7.type;
          (this.gameState as any).winCondition = 'zombie-race';
          // Clear zombification since the zombie has served its purpose (race victory)
          zombieKingOnRow7.zombifiedStatus = { isZombified: false, removeOnTurn: undefined };
          zombieKingOnRow7.isZombified = false;
        }
        return true;
      }
    }
    // Dark side: check row 0 for a zombified light animal if dark king is Fungi
    if (this.gameState.darkKing === PieceType.Fungi) {
      const zombieKingOnRow0 = this.getPiecesInRow(0).find(
        (x) =>
          x.isLight &&
          x.zombifiedStatus.isZombified &&
          x.type !== PieceType.Fungi
      );
      if (zombieKingOnRow0) {
        // CRITICAL FIX: Only modify game state when NOT in simulation
        if (!isSimulation) {
          this.gameState.lightWon = false;
          this.gameState.zombieRacePieceType = zombieKingOnRow0.type;
          (this.gameState as any).winCondition = 'zombie-race';
          // Clear zombification since the zombie has served its purpose (race victory)
          zombieKingOnRow0.zombifiedStatus = { isZombified: false, removeOnTurn: undefined };
          zombieKingOnRow0.isZombified = false;
        }
        return true;
      }
    }

    const row7Pieces = this.getPiecesInRow(7);
    const row0Pieces = this.getPiecesInRow(0);


    var hasLightKingWonRace = row7Pieces.some(
      (x) => x.isLight && x.type === this.gameState.lightKing
    );
    var hasDarkKingWonRace = row0Pieces.some(
      (x) => !x.isLight && x.type === this.gameState.darkKing
    );

    // TUTORIAL FIX: Block race victories in capture-focused tutorials
    const tutorialConfig = (this.gameState as any).tutorialConfig;
    const isInTutorialMode =
      this.gameState.mode === Mode.Tutorial ||
      (this.gameState.mode === Mode.TestAI && tutorialConfig);

    // Phase 2 - SOC Refactoring: Use TutorialSpec instead of hardcoded checks
    if (isInTutorialMode && tutorialConfig) {
      const spec = getTutorialSpec(tutorialConfig.constellation, tutorialConfig.tutorialIndex);

      // Apply blockRaceVictory override
      if (spec.blockRaceVictory) {
        hasLightKingWonRace = false;
        hasDarkKingWonRace = false;
      }

      // Apply blockKingAbsence override
      if (spec.blockKingAbsence) {
        if (!isLightKingPresent) {
          isLightKingPresent = true; // Pretend king is present to prevent auto-loss
        }
        if (!isDarkKingPresent) {
          isDarkKingPresent = true; // Pretend king is present to prevent auto-win
        }
      }

      // Apply customWinConditions override (use TutorialWinConditions exclusively)
      // This is handled in zodiacEngine.ts checkGameEnd() via TutorialEngineAdapter
    }

    // --- ZOMBIE KING SCENARIO ---
    // CRITICAL: When a Fungi king is captured, it zombifies the capturer, making THEM the new king temporarily
    // This is the "zombie king" edge case: the original king (Fungi) is gone, but a zombified non-Fungi
    // piece is now acting as the king for one turn. The game should NOT end yet.
    //
    // Detection logic:
    // 1. Check if the missing king was a Fungi (can zombify on capture)
    // 2. Check if there's a zombified piece that is NOT a Fungi (the new temporary king)
    // 3. If both true: ZOMBIE KING SCENARIO - game continues for one turn

    // Check for ZOMBIE KING SCENARIO when Dark king (Fungi) is missing
    if (!isDarkKingPresent && this.gameState.darkKing === PieceType.Fungi) {
      // Dark Fungi king was captured - check if it zombified the capturer

      // A Monkey that just consumed an enemy Fungi king is "prezombified": the
      // pendingMonkeyZombification flag is set, but actual zombification is deferred
      // until the bonus-move completes (see MovementManager.ts:393-407). Treat the
      // pending Monkey as a zombie king so checkGameEnd doesn't end the game in the
      // gap between the consume and the bonus move.
      const pendingMonkeyId = (this.gameState as any).pendingMonkeyZombification?.pieceId;
      const zombieKing = pieces.find(
        (x) =>
          x.isLight &&
          x.type !== PieceType.Fungi &&
          (
            x.zombifiedStatus?.isZombified ||
            x.isZombified ||
            (pendingMonkeyId !== undefined && x.id === pendingMonkeyId && x.type === PieceType.Monkey)
          )
      );


      if (zombieKing) {

        // Check if zombification is about to expire (this is the zombie's last turn)
        // Use <= turnCount + 1 to detect BOTH:
        // 1. Zombification expiring ON this turn (removeOnTurn === turnCount)
        // 2. Zombification expiring AFTER this turn (removeOnTurn === turnCount + 1)
        // In case 2, the zombie just made their last move - if they didn't win, game ends
        const zombificationExpiring = zombieKing.zombifiedStatus.removeOnTurn! <= this.gameState.turnCount + 1;

        // CRITICAL: Check if the zombie piece has an active bonus turn
        // Zombie penguins get a bonus turn after rotating - they should complete their bonus turn
        // before we consider the zombification "expired"
        const hasBonusTurnActive = this.gameState.activeBonusTurnPieceId === zombieKing.id;

        if (zombificationExpiring && !hasLightKingWonRace && !hasBonusTurnActive) {
          // Zombification expires without reaching race square - Light wins by kingslayer
          if (!isSimulation) {
            this.gameState.lightWon = true;
            (this.gameState as any).winCondition = 'kingslayer';
            // Clear zombification since the zombie's turn is over (kingslayer victory)
            zombieKing.zombifiedStatus = { isZombified: false, removeOnTurn: undefined };
            zombieKing.isZombified = false;
          }
          return true;
        }

        // Zombification still active OR zombie has bonus turn - let the zombie king have its turn
        if (!hasLightKingWonRace) {
          if (hasBonusTurnActive) {
          }
          return false; // Game continues
        }
      }
    }

    // Check for ZOMBIE KING SCENARIO when Light king (Fungi) is missing
    if (!isLightKingPresent && this.gameState.lightKing === PieceType.Fungi) {
      // Light Fungi king was captured - check if it zombified the capturer

      // A Monkey that just consumed an enemy Fungi king is "prezombified": the
      // pendingMonkeyZombification flag is set, but actual zombification is deferred
      // until the bonus-move completes (see MovementManager.ts:393-407). Treat the
      // pending Monkey as a zombie king so checkGameEnd doesn't end the game in the
      // gap between the consume and the bonus move.
      const pendingMonkeyId = (this.gameState as any).pendingMonkeyZombification?.pieceId;
      const zombieKing = pieces.find(
        (x) =>
          !x.isLight &&
          x.type !== PieceType.Fungi &&
          (
            x.zombifiedStatus?.isZombified ||
            x.isZombified ||
            (pendingMonkeyId !== undefined && x.id === pendingMonkeyId && x.type === PieceType.Monkey)
          )
      );


      if (zombieKing) {

        // Check if zombification is about to expire (this is the zombie's last turn)
        // Use <= turnCount + 1 to detect BOTH:
        // 1. Zombification expiring ON this turn (removeOnTurn === turnCount)
        // 2. Zombification expiring AFTER this turn (removeOnTurn === turnCount + 1)
        // In case 2, the zombie just made their last move - if they didn't win, game ends
        const zombificationExpiring = zombieKing.zombifiedStatus.removeOnTurn! <= this.gameState.turnCount + 1;

        // CRITICAL: Check if the zombie piece has an active bonus turn
        // Zombie penguins get a bonus turn after rotating - they should complete their bonus turn
        // before we consider the zombification "expired"
        const hasBonusTurnActive = this.gameState.activeBonusTurnPieceId === zombieKing.id;

        if (zombificationExpiring && !hasDarkKingWonRace && !hasBonusTurnActive) {
          // Zombification expires without reaching race square - Dark wins by kingslayer
          if (!isSimulation) {
            this.gameState.lightWon = false;
            (this.gameState as any).winCondition = 'kingslayer';
            // Clear zombification since the zombie's turn is over (kingslayer victory)
            zombieKing.zombifiedStatus = { isZombified: false, removeOnTurn: undefined };
            zombieKing.isZombified = false;
          }
          return true;
        }

        // Zombification still active OR zombie has bonus turn - let the zombie king have its turn
        if (!hasDarkKingWonRace) {
          if (hasBonusTurnActive) {
          }
          return false; // Game continues
        }
      }
    }

    if (!isDarkKingPresent || hasLightKingWonRace) {
      // CRITICAL FIX: Only modify game state when NOT in simulation
      if (!isSimulation) {
        this.gameState.lightWon = true;
        // Set win condition - only set zombieRace flags for actual zombie race wins (handled above)
        if (hasLightKingWonRace) {
          (this.gameState as any).winCondition = 'race';
        } else {
          (this.gameState as any).winCondition = 'kingslayer';
        }
      }
      return true;
    }

    if (!isLightKingPresent || hasDarkKingWonRace) {
      // CRITICAL FIX: Only modify game state when NOT in simulation
      if (!isSimulation) {
        this.gameState.lightWon = false;
        // Set win condition - only set zombieRace flags for actual zombie race wins (handled above)
        if (hasDarkKingWonRace) {
          (this.gameState as any).winCondition = 'race';
        } else {
          (this.gameState as any).winCondition = 'kingslayer';
        }
      }
      return true;
    }

    // Check for threefold repetition (draw condition)
    if (!isSimulation && this.fenHistoryManager.hasThreefoldRepetition(this.gameState)) {
      this.gameState.state = State.Draw;
      (this.gameState as any).drawReason = "3 time repeat draw";
      (this.gameState as any).winCondition = 'draw-3-peat';
      return true;
    }


    // Skip stalemate detection during bonus turns - the bonus turn holder should continue their turn
    if (this.gameState.activeBonusTurnPieceId) {
      return false; // Game continues, no draw/stalemate
    }

    // Draw condition: If the NEXT player has their king alive, the enemy has not completed a race, and the next player has zero legal moves
    const MovementManager = require("./MovementManager").MovementManager;
    const movementManager = new MovementManager(this);
    const nextPlayerIsLight = !this.gameState.isLightTurn;
    const nextPlayerHasKing = nextPlayerIsLight
      ? isLightKingPresent
      : isDarkKingPresent;
    const enemyHasNotCompletedRace = nextPlayerIsLight
      ? !hasDarkKingWonRace
      : !hasLightKingWonRace;
    if (nextPlayerHasKing && enemyHasNotCompletedRace) {
      let hasLegalMove = false;
      for (let row = 0; row < this.gameState.board.length; row++) {
        for (let col = 0; col < this.gameState.board[row].length; col++) {
          const position = { row, col };
          const pieces = this.getPiecesAt(position);
          for (const piece of pieces) {
            if (piece.isLight === nextPlayerIsLight) {
              const moves = movementManager.getValidMoves(position, piece);
              if (moves.length > 0) {
                hasLegalMove = true;
                break;
              }
            }
          }
          if (hasLegalMove) break;
        }
        if (hasLegalMove) break;
      }
      if (!hasLegalMove) {
        // PATCH: If a bonus turn is active, do not trigger draw/stalemate. End the bonus turn and pass the turn.
        if (this.gameState.activeBonusTurnPieceId) {
          // CRITICAL FIX: Only modify game state when NOT in simulation
          if (!isSimulation) {
            // End the bonus turn and pass the turn
            this.clearBonusTurn(); // This clears both skipTurnChange and activeBonusTurnPieceId
            this.changeTurns();
          }
          return false; // Do not end the game, let the next player move
        }

        // Clean approach: For tutorial mode, delegate stalemate handling to tutorial system
        if (isInTutorialMode && tutorialConfig) {
          // CRITICAL FIX: Only emit event when NOT in simulation
          if (!isSimulation) {
            // Call tutorial stalemate callback - let tutorial system decide what to do.
            // Use realConstellation/realTutorialIndex when present: kingless tutorials
            // are remapped to Tree[1] at the engine boundary for blockKingAbsence, but
            // downstream consumers (e.g. TutorialStalemateHandler.isTutorialSolo) need
            // the REAL identity or they misclassify Bear[0] solo as non-solo Tree[1].
            if (this.tutorialStalemateCallback) {
              this.tutorialStalemateCallback({
                constellation: tutorialConfig.realConstellation || tutorialConfig.constellation,
                tutorialIndex: tutorialConfig.realTutorialIndex !== undefined
                  ? tutorialConfig.realTutorialIndex
                  : tutorialConfig.tutorialIndex,
                nextPlayerIsLight,
                gameState: this.gameState,
                message: `No legal moves available for ${
                  nextPlayerIsLight ? "Light" : "Dark"
                } player`,
              });
            }
          }

          // Always let tutorial system handle the end game logic
          return false;
        }

        // DEBUG: Log why stalemate is being triggered
        // eslint-disable-next-line no-console
        // eslint-disable-next-line no-console
        if (this.gameState.activeBonusTurnPieceId) {
          try {
            const lockedPiece = this.findPieceById(
              this.gameState.activeBonusTurnPieceId
            );
            // eslint-disable-next-line no-console
          } catch (e) {
            // eslint-disable-next-line no-console
          }
        }
        // CRITICAL FIX: Only modify game state when NOT in simulation
        if (!isSimulation) {
          this.gameState.state = State.Draw;
          // Only set drawReason if not already set to repetition draw
          if ((this.gameState as any).drawReason !== "3 time repeat draw") {
            (this.gameState as any).drawReason = "No legal moves remain";
            (this.gameState as any).winCondition = 'draw-stalemate';
          }
        }
        return true;
      }
    }
    return false;
  }

  // Set callback for tutorial stalemate events
  setTutorialStalemateCallback(callback: (data: any) => void): void {
    this.tutorialStalemateCallback = callback;
  }

  /**
   * Stalemate-only check for non-solo tutorials, where the full hasGameEnded()
   * is skipped to prevent premature PostGame from blocking AI moves. Mirrors
   * the stalemate branch of hasGameEnded but runs no victory logic, and fires
   * the tutorialStalemateCallback so TutorialGame can show the loss UI.
   * Call BEFORE changeTurns — uses `nextPlayerIsLight = !isLightTurn`.
   */
  hasNextPlayerStalemate(): boolean {
    if (this.gameState.activeBonusTurnPieceId) return false;

    const tutorialConfig = (this.gameState as any).tutorialConfig;
    if (!tutorialConfig) return false;

    const nextPlayerIsLight = !this.gameState.isLightTurn;

    const pieces = this.getAllPieces();
    const nextPlayerHasKing = nextPlayerIsLight
      ? pieces.some((p) => p.isLight && p.type === this.gameState.lightKing)
      : pieces.some((p) => !p.isLight && p.type === this.gameState.darkKing);
    if (!nextPlayerHasKing) return false;

    const MovementManager = require("./MovementManager").MovementManager;
    const movementManager = new MovementManager(this);

    for (let row = 0; row < this.gameState.board.length; row++) {
      for (let col = 0; col < this.gameState.board[row].length; col++) {
        const position = { row, col };
        const piecesAtPos = this.getPiecesAt(position);
        for (const piece of piecesAtPos) {
          if (piece.isLight === nextPlayerIsLight) {
            const moves = movementManager.getValidMoves(position, piece);
            if (moves.length > 0) {
              return false;
            }
          }
        }
      }
    }

    // End the game directly. Setting PostGame + lightWon lets two things happen:
    // 1. The AI Thinking overlay (which hides on state !== Playing) hides immediately.
    // 2. The React effect in TutorialGame that watches `state === PostGame &&
    //    lightWon === false` will trigger the loss popup as a backstop in case
    //    the stalemateCallback path fails or fires too late.
    this.gameState.state = State.PostGame;
    this.gameState.lightWon = !nextPlayerIsLight;
    (this.gameState as any).winCondition = 'tutorial-stalemate';

    if (this.tutorialStalemateCallback) {
      this.tutorialStalemateCallback({
        constellation: tutorialConfig.realConstellation || tutorialConfig.constellation,
        tutorialIndex: tutorialConfig.realTutorialIndex !== undefined
          ? tutorialConfig.realTutorialIndex
          : tutorialConfig.tutorialIndex,
        nextPlayerIsLight,
        gameState: this.gameState,
        message: `No legal moves available for ${
          nextPlayerIsLight ? "Light" : "Dark"
        } player`,
      });
    }

    return true;
  }

  /**
   * Grant a bonus turn to a specific piece
   * Clean separation: only handles game state, no UI concerns
   */
  grantBonusTurn(piece: IPiece): void {
    this.gameState.skipTurnChange = true;
    this.gameState.activeBonusTurnPieceId = piece.id;
  }

  /**
   * Clear bonus turn state and allow turn changes
   */
  clearBonusTurn(): void {
    this.gameState.skipTurnChange = false;
    this.gameState.activeBonusTurnPieceId = null;

    // Clear swing return block when bonus turn ends
    this.clearSwingReturnBlock();
  }

  /**
   * Set the immediate swing return block
   * This blocks one specific monkey from swinging back to where it just swung from
   */
  setSwingReturnBlock(fromPosition: Position, toPosition: Position, monkeyId: string): void {
    this.gameState.lastSwingFromPosition = fromPosition;
    this.gameState.lastSwingToPosition = toPosition;
    this.gameState.lastSwingMonkeyId = monkeyId;
  }

  /**
   * Clear the swing return block
   * Called whenever ANY move is made during bonus turn (except the swing that created the block)
   */
  clearSwingReturnBlock(): void {
    if (this.gameState.lastSwingFromPosition) {
    }
    this.gameState.lastSwingFromPosition = null;
    this.gameState.lastSwingToPosition = null;
    this.gameState.lastSwingMonkeyId = null;
  }
}
